import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { useId, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { HugeIcon } from './HugeIcon';
import { Button } from './Button';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export type SelectOption<T extends string = string> = { label: string; value: T };

export function CustomSelect<T extends string>({
  ariaLabel,
  clearable = false,
  clearLabel = 'Clear selection',
  className,
  defaultValue,
  fullWidth = false,
  label,
  leadingIcon,
  onChange,
  onClear,
  options,
  placeholder,
  value,
  variant = 'field',
}: {
  ariaLabel?: string;
  clearable?: boolean;
  clearLabel?: string;
  className?: string;
  defaultValue?: T;
  fullWidth?: boolean;
  label?: string;
  leadingIcon?: ReactNode;
  onChange?: (value: T) => void;
  onClear?: () => void;
  options: ReadonlyArray<SelectOption<T>>;
  placeholder?: string;
  value?: T;
  variant?: 'field' | 'compact' | 'pill' | 'toolbar';
}) {
  const id = useId();
  const [internalValue, setInternalValue] = useState<T | undefined>(defaultValue ?? options[0]?.value);
  const selectedValue = value ?? internalValue;
  const selected = options.find((option) => option.value === selectedValue);

  return (
    <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <div className="flex items-center gap-1">
        <Select
          onValueChange={(next) => {
            const nextValue = String(next) as T;
            setInternalValue(nextValue);
            onChange?.(nextValue);
          }}
          value={selectedValue}
        >
          <SelectTrigger
            aria-label={ariaLabel}
            className={cn(
              fullWidth && 'w-full',
              variant === 'pill' && 'h-8 rounded-full bg-muted px-4 hover:bg-muted/80',
              variant === 'toolbar' && 'bg-background',
              className
            )}
            id={id}
            size={variant === 'field' ? 'default' : 'sm'}
          >
            {leadingIcon}
            <SelectValue placeholder={placeholder}>{selected?.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {clearable && selected ? (
          <Button aria-label={clearLabel} onClick={() => { setInternalValue(undefined); onClear?.(); }} size="icon-sm" type="button" variant="ghost">
            <HugeIcon icon={Cancel01Icon} size={13} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
